import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { CallController } from '@gem/call/CallController/CallController';
import { IOnDeleteCallParams } from '@gem/call/CallController/CallController.types';
import { Call } from '@gem/call/CallStore/Call';
import { CallView } from '@gem/call/CallView/CallView';
import { ICallContext } from '@gem/call/CallView/CallView.context';
import { CallProviderType } from '@gem/call/Model/callModel.types';

import Tasks from 'APP/Tasks';
import { ProjectUrls } from 'APP/packages/projectUrls/ProjectUrls';
import { RouterLink } from 'APP/router/constants';
import { buildPath } from 'APP/router/helpers/buildPath';
import { UnauthorizedRouts } from 'APP/unauthorized/UnauthorizedView.constants';
import Entities from 'STORE';
import { MainCall } from 'STORE/Calls/MainCall/MainCall';

interface IUnauthorizedOngoingCallProps {
  mainCall: MainCall;
  onVideoAudioStarted?: () => void;
}

export const UnauthorizedOngoingCall = observer<IUnauthorizedOngoingCallProps>(
  ({ mainCall, onVideoAudioStarted }) => {
    const [state, setState] = useState<ICallContext>({} as ICallContext);
    const isStarted = useRef(false);
    const navigate = useNavigate();

    const onDeleteCall = ({ justILeaveCall }: IOnDeleteCallParams): void => {
      if (!mainCall) {
        return;
      }
      Entities.Calls.delete(mainCall.groupId, mainCall.channelId);

      if (justILeaveCall) {
        navigate(
          buildPath([RouterLink.Unauthorized, UnauthorizedRouts.CallInvitation], {
            channelId: mainCall.channelId,
          }),
          { replace: true }
        );
        return;
      }

      navigate(
        buildPath([RouterLink.Unauthorized, UnauthorizedRouts.CallFinished], {
          channelId: mainCall.channelId,
        }),
        { replace: true }
      );
    };

    useEffect(() => {
      const call = new Call({
        myId: mainCall.myId,
        channelId: mainCall.channelId,
        isUnauthorizedCall: mainCall.isUnauthorizedCall,
        providerType: CallProviderType.Livekit,
        initialDevicesIds: mainCall.initialDevicesIds,
        mediaPermissions: mainCall.mediaPermissions,
        maxParticipants: Entities.Calls.maxParticipants,
        initialMeOpponentAvatar: {
          title: Entities.callInvitation.selfName,
          url: null,
          colorIndex: null,
        },
      });

      const callController = new CallController({
        call,
        providerUrl: ProjectUrls.liveKitCallsUrl,
        handlers: {
          feedbackHandler: Tasks.feedback.checkStarsFeedback,
          onDeleteCall,
          onFirstVideoAudioStarted: onVideoAudioStarted,
        },
      });
      if (mainCall.isWaitingRoomEnabled) {
        call.waitingRoom.setEnabled(mainCall.isWaitingRoomEnabled);
      }
      call.setGuestsAllowed(mainCall.isGuestsAllowed);
      call.viewState.expand();

      setState({ call, callController });
      mainCall.setDeleteCallHandler(async () => {
        if (callController) {
          await callController.dispose(false, true);
        }
      });

      return (): void => {
        if (state.call) {
          state.call.dispose();
        }
      };
    }, []);

    useEffect(() => {
      if (mainCall.readyToStart && mainCall.callInfo && state.call && !isStarted.current) {
        state.callController.initCallStateService(mainCall.callInfo.channelId);
        isStarted.current = true;
        state.callController.startCall({
          callInfo: mainCall.callInfo,
          withoutTimer: mainCall.withoutTimer,
          isVideoMuted: !mainCall.initialMedia.withVideo,
          isAudioMuted: !mainCall.initialMedia.withAudio,
        });
        state.call.setAvatar({
          title: mainCall.callInfo.callName || null,
          url: null,
          colorIndex: null,
        });
      }
    }, [mainCall.readyToStart, state.call]);

    useEffect(() => {
      if (!state.call) {
        return;
      }
      if (state.call.viewState.isFullScreen) {
        Tasks.app.fullscreen.registerFullscreen(state.call.viewState.collapse);
      } else {
        Tasks.app.fullscreen.unregisterFullscreen(state.call.viewState.collapse);
      }

      return (): void => {
        Tasks.app.fullscreen.unregisterFullscreen(state.call.viewState.collapse);
      };
    }, [state.call?.viewState.isFullScreen]);

    if (!state.call) {
      return null;
    }

    return <CallView state={state} />;
  }
);
